// Central per-entry isolation for the PowerSync upload queue.
//
// THE WEDGE THIS PREVENTS. `uploadData()` acks the whole CRUD batch with a
// single `batch.complete()` that runs ONLY after every entry settles. So a
// single queued entry that throws a NON-TRANSIENT error blocks every later
// mutation on the device forever — the SDK retries the same batch every few
// seconds indefinitely. That is the production "capture session/item not
// found" wedge: a `capture_sessions` complete/reopen that 404s (the session
// was cancelled or admin-deleted server-side), or a session insert whose site
// is gone, sits in the batch behind already-orphaned item entries and keeps
// the batch from ever completing — so the item carve-outs' `quarantined` /
// `dropped` telemetry re-fires on every retry while nothing drains.
//
// The per-op dispatchers each carve out their OWN known-permanent cases first
// (item create/patch not_found → quarantine; session 409 → re-home/drop).
// This module is the BACKSTOP for everything they (deliberately) rethrow: a
// permanent error on an op type without a carve-out, or a code a dispatcher
// did not anticipate, is isolated here so the queue drains instead of wedging
// — while still PRESERVING any count the entry carried (never silently drop a
// count — AGENTS.md "Business criticality").
//
// Conservative by construction. Only an `ApiError` whose Encore code can NEVER
// succeed on retry with the same payload is treated as permanent. Everything
// else — a transient 5xx, a network failure, `"Client has already been
// closed"` (the wa-sqlite worker torn down under a backgrounded mobile tab),
// the connector's own unsupported-op guard `Error`, or any unknown throw — is
// reported as NOT permanent, so `uploadData` rethrows and the SDK retries the
// whole batch exactly as before. We would rather over-retry a thousand times
// than ack-and-drop a write that could still land.
//
// THIS INVARIANT IS NOW A GATE (Epic #681 Pillar 3). "No dispatcher may throw
// on an EXPECTED condition" is enforced statically by
// scripts/lint-powersync-ack-quarantine.mjs, which reads the per-dispatcher
// disposition registry in scripts/powersync-dispositions.mjs and CROSS-CHECKS
// every `permanent-drain` claim against the table-aware permanent-code sets
// below — so a registry entry can never claim a drain the classifier won't
// honour (#715). The runtime counterpart is the real-engine `uploadData`
// contract test family
// (apps/frontend/src/hooks/powersync/__tests__/uploaddata-contract*.test.ts,
// #716). If you change PERMANENT_UPLOAD_CODES / PERMANENT_SESSION_CODES /
// PERMANENT_ITEM_CODES / PERMANENT_FLOORS_CODES, update the registry in lockstep
// — the gate will fail otherwise.

import type { CrudEntry } from '@powersync/web'
import type { PowerSyncWriteDb } from './powersync-db'
import * as Sentry from '@sentry/react'

import { ApiError } from '../api'

import { invalidFieldsFrom } from './connector-error-details'
import { quarantineUnsyncableCount } from './connector-quarantine'
import { _emitDropped } from './upload-events'

// What the central backstop treats as permanent (isolate + drain) is
// TABLE-AWARE, because "permanent" is not uniform across ops:
//
//   not_found — permanent for EVERY table. A 404 (FK parent / session / space
//     gone, or an orphan id that never synced) can never succeed on retry and no
//     dispatcher has a keep-queued contract for it, so draining it (preserving
//     any count via quarantine) is always right — the safe universal backstop.
//
//   invalid_argument — permanent for capture_sessions AND floors (different
//     causes, same disposition: drain). capture_sessions: a session insert whose
//     site left the active contract can never land (the #656 class; createSession
//     can 400 invalid_argument — Codex on PR 660). floors: a floor PUT whose NAME
//     is no longer an active floor option — an admin renamed/deleted the option
//     during the field's offline window, so resolveFloorCore returns
//     `unknown_option` and the endpoint 400s `invalid_argument`
//     (apps/backend/src/capture/resolve-floor.ts). Neither holds a captured count
//     of its own (a session's child capture_items, and a floor's dependent room,
//     each quarantine their own counts on their 404s) and neither dispatcher has
//     a keep-queued contract — so rethrowing forever would only WEDGE the queue
//     (floors: C6/#692, every capture queued behind the floor stuck while the UI
//     showed "saved"). Dropping an off-catalogue floor is acceptable per PRD §6.5
//     / ADR-015 — a floor name is admin reference data, not contractual count
//     loss. The dependent New-Room space PUT drains too: createSpace maps its
//     now-dangling floor FK to not_found (C6/#692, create-space.ts), which spaces
//     already treats as a permanent drain — so the whole cascade clears instead
//     of re-wedging one entry downstream.
//
//   permission_denied — permanent for capture_sessions AND capture_items (a write
//     the role may not make can never land with the same role; no keep-queued
//     contract). capture_sessions: a session the role may not write. capture_items
//     (#751): a count whose session owner does not match the surveyor — update-item.ts
//     / create-item.ts raise permission_denied ONLY on an owner mismatch,
//     which is FROZEN across retries (the role/ownership won't change on retry with
//     the same payload). For FLOORS and every other table it stays TRANSIENT
//     (rethrow): a genuine session ownership/state failure must surface, not silently
//     drain (C6/#692 deliberately scopes the floors drain to invalid_argument only).
//
//   capture_items (#751) — invalid_argument AND permission_denied are BOTH permanent
//     (drain via the device-local quarantine, never silently dropped). Why this is
//     safe and does not destroy a still-held server row:
//       • For capture_items, isolatePoisonEntry routes through quarantineUnsyncableCount
//         (below): it COPIES the payload into the localOnly orphaned_capture_items table
//         (survives restarts, recoverable) BEFORE removing the un-syncable local row, then
//         emits `quarantined`. The count is preserved, never dropped — the existing
//         not_found behaviour, extended to two more frozen-permanent codes.
//       • invalid_argument on /capture/items is ONLY ever a frozen-bad payload (Zod, a
//         cross-building space, a soft-rejected asset type I-API-8, a grouping-rule
//         failure — create-item.ts) — it can never land with the same body.
//       • permission_denied is ONLY ever a session owner mismatch (create-item.ts,
//         update-item.ts) — it can never land with the same role.
//       • A still-held SERVER row is never destroyed: quarantine ends with a LOCAL
//         `DELETE FROM capture_items`, and the connector's capture_items DELETE branch
//         (connector.ts) is an UNCONDITIONAL local-only no-op ack — it issues NO server
//         DELETE. So even on a PATCH where the server still holds the row
//         (permission_denied), the local delete drains the queue but leaves the
//         authoritative row to re-sync down (LWW). Net: queue unblocks, no infinite
//         retry, no silent count loss, no server-row destruction (neutralises the
//         Codex-656 concern). failed_precondition (the LWW signal) is UNTOUCHED and stays
//         transient, so last-write-wins reconciliation is unchanged.
//     NOTE the create-item dispatcher ALSO drains invalid_argument INLINE (a broad
//     quarantine carve-out — connector-dispatch-create-item.ts, #738 residual #2), so on
//     the create path PERMANENT_ITEM_CODES is a defence-in-depth BACKSTOP; the PATCH path
//     has no inline invalid_argument carve-out, so the classifier is the drain there.
//
//   For every OTHER table (non-session, non-item, non-floors) invalid_argument and
//     permission_denied MUST rethrow HERE, because a blanket drain would break a
//     deliberate dispatcher contract: connector-dispatch-space-delete.ts rethrows
//     403 to keep the delete + its pending_space_deletes tombstone queued; and a
//     table-wide drain that deleted a SPACES/FLOORS row on a validation/ownership 4xx
//     would lose a row the server still holds (Codex on PR 656). SPACES is the
//     subtle one: the CREATE path drains its OWN invalid_argument INLINE (a
//     rejected room-type — connector-dispatch-space.ts, issue 785) rather than
//     table-wide, PRECISELY so this classifier keeps spaces invalid_argument
//     transient — a spaces ALIAS PATCH (connector-dispatch-space-update.ts)
//     rethrows invalid_argument too, and a table-wide spaces drain would make
//     isolatePoisonEntry DELETE the room over a bad alias edit. (audit-1 #2,
//     refined for Codex on PR 660; floors invalid_argument added under C6/#692;
//     spaces create-path inline drain under issue 785; capture_items
//     invalid_argument + permission_denied added under #751)
//
// Always transient (rethrow → SDK retries the whole batch): failed_precondition
// (LWW signal, reconciled in place) · already_exists (re-homed) · unauthenticated
// (a token refresh fixes it) · 5xx / unavailable / unknown.
const PERMANENT_UPLOAD_CODES: ReadonlySet<string> = new Set(['not_found'])
const PERMANENT_SESSION_CODES: ReadonlySet<string> = new Set([
  'not_found',
  'invalid_argument',
  'permission_denied',
])
// capture_items (#751): not_found (the universal backstop) + invalid_argument
// (a frozen-bad count payload — Zod / cross-building space / soft-rejected asset
// type I-API-8 / grouping; create-item.ts) + permission_denied (a session owner
// mismatch — create-item.ts, update-item.ts). All three are frozen
// across retries and the count is PRESERVED in the device-local quarantine
// (never dropped). Deliberately NOT failed_precondition — that is the LWW
// stale-version signal and MUST stay transient so last-write-wins reconciles
// (update-item.ts). See the header block for the full safety reasoning.
const PERMANENT_ITEM_CODES: ReadonlySet<string> = new Set([
  'not_found',
  'invalid_argument',
  'permission_denied',
])
// floors: not_found (the universal backstop) + invalid_argument (off-catalogue
// floor name — C6/#692). Deliberately NOT permission_denied / failed_precondition
// — those stay transient so a genuine ownership/state failure surfaces instead of
// silently draining an admin-curated floor.
const PERMANENT_FLOORS_CODES: ReadonlySet<string> = new Set([
  'not_found',
  'invalid_argument',
])

/**
 * True only for an `ApiError` whose Encore code can never succeed on retry for
 * the given `table`. Used by `uploadData` to decide whether to isolate a failing
 * entry (drain it) or rethrow (keep the whole batch queued for the SDK to
 * retry). Conservative: anything not recognised-permanent for its table returns
 * false so the caller retries rather than risk dropping a still-uploadable write
 * or breaking a dispatcher's keep-queued / surface contract.
 *
 * STATUS FALLBACK (H3 / #694): a 404 with NO Encore `code` (a proxy/CDN/gateway
 * in front of Encore served its OWN HTML/empty body) is classified as not_found
 * by HTTP status — see the inline note. Without it a code-less 404 fell through
 * to `false`, and uploadData's bare rethrow wedged the whole device queue.
 */
export function isPermanentUploadError(err: unknown, table?: string): boolean {
  if (!(err instanceof ApiError)) return false
  // A CODE-LESS 404 — `err.status === 404` with no Encore body `code` — is what a
  // proxy / CDN / gateway / load-balancer in front of Encore returns when IT
  // serves the 404 (an HTML or empty body, so apiFetch surfaces no `code`; common
  // in prod). At the HTTP layer it IS a not_found that can NEVER succeed on retry
  // with the same payload, and no dispatcher has a keep-queued contract for a 404
  // (the session/space DELETE paths already ack their own 404 BY STATUS, so they
  // never reach this backstop). Map it to `not_found` — permanent for EVERY table
  // (the universal backstop documented above) — so the entry DRAINS (a
  // capture_items count preserved in quarantine) instead of the bare rethrow in
  // uploadData wedging the whole device queue behind it while the UI shows
  // "✓ Saved". Any OTHER code-less error (e.g. a code-less 5xx) stays
  // unclassified → transient rethrow, so the conservative "rethrow when in doubt"
  // default is unchanged everywhere else.
  //
  // 404-WINS (audit-2): a 404 maps to not_found regardless of any body `code`, so
  // this stays consistent with `isNotFoundError(err)` (api.ts, `status === 404`)
  // which the dispatcher inline carve-outs use. Without it, a (rare) gateway/WAF
  // 404 carrying a NON-Encore code — e.g. `{ "code": "GATEWAY_ERROR" }` — would be
  // drained inline by the dispatchers but RETHROWN here, wedging a reference-data
  // INSERT (spaces/floors/capture_sessions) that has no inline 404 carve-out.
  const code = err.status === 404 ? 'not_found' : err.code
  if (code === undefined) return false
  // Table-aware permanence (uses the 404-normalised `code`): capture_sessions,
  // capture_items, and floors each carry extra permanent codes beyond the
  // universal not_found. floors invalid_argument is C6/#692 (off-catalogue floor
  // name drains); capture_items invalid_argument + permission_denied is #751 (a
  // frozen-bad count payload / session owner mismatch — count preserved in the
  // quarantine, no server-held row destroyed since the capture_items DELETE acks
  // local-only).
  if (table === 'capture_sessions') return PERMANENT_SESSION_CODES.has(code)
  if (table === 'capture_items') return PERMANENT_ITEM_CODES.has(code)
  if (table === 'floors') return PERMANENT_FLOORS_CODES.has(code)
  return PERMANENT_UPLOAD_CODES.has(code)
}

/**
 * Coerce any throwable into the non-PII `{ code, status }` pair the upload
 * telemetry reports. The jam beacon and the isolation backstop both derive these
 * two fields the same way, so the derivation lives here once instead of being
 * transcribed at each call site. This shares ONLY the `{code,status}` coercion —
 * NOT a unified no-PII contract: the jam beacon captures a synthetic, payload-free
 * error, whereas the isolation/quarantine path is a separate disposition with its
 * own payload-handling rules (it deliberately preserves the count for recovery).
 */
function errorCodeStatus(err: unknown): {
  code: string
  status: number | undefined
} {
  if (err instanceof ApiError) {
    return { code: err.code ?? 'unknown', status: err.status }
  }
  return { code: 'unknown', status: undefined }
}

/**
 * Stable per-entry key for the jam edge-trigger — ids/op/table only, no PII.
 * EXPORTED (audit-1 #5) so the jam tests derive their expected Sentry
 * `fingerprint` key segment from this same helper instead of hand-spelling
 * `'capture_items:PATCH:<id>'` literals that can silently drift from prod. It is
 * a pure, non-PII function over public CrudEntry fields, so exporting it widens
 * no surface that matters — it only removes a literal-drift hazard.
 */
export function jamKeyFor(entry: CrudEntry): string {
  return `${entry.table}:${entry.op}:${entry.id}`
}

/**
 * Entries CURRENTLY observed as jammed (beaconed, not yet drained). Module-level
 * so it survives the SDK's retry loop: `_uploadAllCrud` re-invokes `uploadData`
 * on the SAME wedged batch every `DEFAULT_RETRY_DELAY_MS` (5 s, no backoff), so
 * an UNGUARDED beacon would fire ~once per 5 s forever per stuck device —
 * burning Sentry quota, tripping the error-ingest rate-limit (which then drops
 * the device's OTHER real errors too), and corrupting the very stall-rate signal
 * the beacon exists to produce. The beacon is therefore EDGE-triggered: it fires
 * once when an entry first wedges, and again only after that entry drains
 * (success or isolation clears its key — see `clearUploadJam`).
 */
const jammedEntries = new Set<string>()

/**
 * Whether ANY entry is currently jammed. Lets the clean-dispatch happy path skip
 * `clearUploadJam`'s key-string build + `Set.delete` when nothing was ever
 * jammed (audit-1 #7) — the common case is an empty jam set, so a per-entry clear
 * there is pure waste. Safe to gate on: if the set is empty there is nothing to
 * clear. The module-private `jammedEntries` stays encapsulated.
 */
export function hasUploadJams(): boolean {
  return jammedEntries.size > 0
}

/**
 * Clear an entry's jam key once it has drained (dispatched cleanly or isolated),
 * so a later re-occurrence re-beacons as a new episode. Called by `uploadData`.
 */
export function clearUploadJam(entry: CrudEntry): void {
  jammedEntries.delete(jamKeyFor(entry))
}

/**
 * Prune the jam set to the entries still queued. An entry can leave the queue
 * WITHOUT draining through `clearUploadJam` — locally deleted, reconciled, or
 * superseded while wedged — and its key would otherwise linger for the JS-context
 * lifetime, silently suppressing a future genuine wedge on a recycled
 * `(table:op:id)` (and slowly growing the Set). Calling this at the top of each
 * `uploadData` pass keeps the invariant "in `jammedEntries` ⟺ currently queued"
 * and bounds the Set to the page size. (Within-pass drains are still cleared
 * promptly by `clearUploadJam` so an entry can re-arm in the same session.)
 */
export function reconcileUploadJams(entries: readonly CrudEntry[]): void {
  if (jammedEntries.size === 0) return
  const live = new Set(entries.map(jamKeyFor))
  for (const key of jammedEntries) {
    if (!live.has(key)) jammedEntries.delete(key)
  }
}

/** Test seam: reset the module-level jam set between scenarios. */
export function _resetUploadJams(): void {
  jammedEntries.clear()
}

/**
 * Beacon a NON-PERMANENT upload error that is about to rethrow out of
 * `uploadData` and WEDGE the whole CRUD batch: the rethrow skips
 * `batch.complete()`, so the SDK retries the entire batch every ~5 s and every
 * entry after this one is blocked behind it — the production "upload jam".
 *
 * Historically this rethrow produced NO Sentry signal and NO in-app surface (the
 * SDK only records it on `SyncStatus.dataFlow.uploadError` and logs at debug), so
 * weeks of stalled devices were invisible to ops and to the surveyor.
 *
 * EDGE-TRIGGERED (see `jammedEntries`): fires once per wedge episode PER APP
 * SESSION, not once per retry — "a jam started / recurred", which is what the
 * stall-rate metric needs (a per-tick counter cannot measure distinct wedges).
 * The Set is in-memory, so an app reload re-arms it; a still-wedged device thus
 * emits ~once per launch, not once per 5 s — and distinct wedges remain
 * countable by grouping on `entryId` in Sentry.
 *
 * NO PII (this beacon only) — the `extra` is a hand-built 12-key allowlist:
 * `reason` · `jammed` · `table` · `op` · `entryId` · `indexInPage` · `pageSize` ·
 * `hasMore` · `status` · `code` — ids / op / table / Encore code / HTTP status /
 * page position ONLY (AGENTS.md "Business criticality" + the observability
 * non-PII contract); NEVER the payload (quantity, notes, display names, free
 * text). The 11th key, `invalidFields`, carries the Zod field PATH(S) the backend
 * rejected for an `invalid_argument` (e.g. `["quantity"]`) — schema KEYS, never
 * captured values (`zodIssuePaths` guarantees it), `undefined` otherwise (Sentry
 * drops it). It turns an opaque `invalid_argument` wedge into "rejected on
 * <field>". The 12th key, `errorName` (#771), carries the thrown error's CLASS
 * NAME ONLY (`'TypeError'` / `'Error'` / `'ApiError'`; `'unknown'` for a non-Error
 * throw) — NEVER `err.message`. It splits the opaque `code: 'unknown'` non-ApiError
 * bucket by cause (network fail vs torn-down worker vs a bug). Keep this list in
 * lockstep with the `extra` object below AND with ALLOWED_EXTRA_KEYS in
 * scripts/lint-powersync-jam-beacon-no-pii.mjs — the M2 real-engine contract test
 * pins the exact key-set (audit-1 #2). Two guards make the no-PII property hold by
 * CONSTRUCTION, not by Sentry config: that allowlist, AND we capture a SYNTHETIC
 * error — never the raw `ApiError`, whose `.message` would otherwise ship.
 *
 * Because that synthetic error is identical across entries (same message + stack
 * + call site), Sentry's default `dedupeIntegration` would drop a genuinely
 * DISTINCT second wedge as a "duplicate" of the first — re-introducing the silent
 * loss the edge-trigger exists to prevent. A PER-ENTRY `fingerprint` keyed on the
 * non-PII `jamKeyFor` keeps distinct wedges distinct (and gives each its own
 * Sentry issue); the `area: powersync_upload_jam` tag stays the queryable handle.
 *
 * Best-effort and synchronous: wrapped so it can never throw or change the
 * rethrow that follows it.
 */
export function beaconUploadJam(
  err: unknown,
  entry: CrudEntry,
  indexInPage: number,
  pageSize: number,
  hasMore: boolean,
): void {
  try {
    const key = jamKeyFor(entry)
    // Edge-trigger: skip if this entry is already a known, still-active jam.
    if (jammedEntries.has(key)) return

    const { code, status } = errorCodeStatus(err)
    // The Zod field path(s) the backend rejected — populated ONLY for an
    // `invalid_argument` carrying structured `details.invalidFields` (field
    // NAMES only — see `invalidFieldsFrom`), `undefined` for every other jam.
    // A FLAT key (not a conditional spread) on purpose: the no-PII gate
    // (lint:powersync-jam-beacon-no-pii) statically resolves the `...fields`
    // spread and allowlist-checks each key, but cannot see into a conditional
    // spread — so a flat, allowlisted key is the only form it can ENFORCE. The
    // `undefined` value carries nothing and Sentry drops it on the wire, so the
    // field only materialises for a validation wedge.
    const invalidFields = invalidFieldsFrom(err)
    // ONE non-PII field object, shared by both sinks so they cannot diverge.
    // `pageSize`/`hasMore` describe the SDK CRUD PAGE (capped at
    // DEFAULT_CRUD_BATCH_LIMIT = 100), NOT the full device queue depth: a wedged
    // device with thousands of queued rows still reports pageSize ≤ 100, so
    // `hasMore` signals the backlog extends beyond this page.
    // The error CLASS NAME ONLY — NEVER `err.message` / the raw `err`. Splits the
    // opaque `code:'unknown'` non-ApiError bucket by cause (see doc block). (#771)
    const errorName = err instanceof Error ? err.name : 'unknown'
    const fields = {
      table: entry.table,
      op: entry.op,
      entryId: entry.id,
      indexInPage,
      pageSize,
      hasMore,
      status,
      code,
      invalidFields,
      errorName,
    }
    // Capture a SYNTHETIC error (never `err`) — see the no-PII note above. The
    // per-entry fingerprint defeats Dedupe collapsing distinct wedges.
    Sentry.captureException(new Error('powersync_upload_jam'), {
      level: 'warning',
      tags: { area: 'powersync_upload_jam' },
      fingerprint: ['powersync_upload_jam', key],
      extra: { reason: 'powersync_upload_jam', jammed: true, ...fields },
    })
    // Latch the key ONLY AFTER the Sentry emit succeeds: if `captureException`
    // threw (Sentry offline / rate-limited — correlated with the very network
    // failure that causes the wedge), leaving the key UNLATCHED lets the next
    // ~5 s retry re-attempt the beacon instead of going permanently silent for
    // the whole episode. The local console sink is best-effort and below the
    // latch so its failure can never trigger a duplicate Sentry emit.
    jammedEntries.add(key)
    console.warn(
      '[powersync] upload JAM — non-permanent error will rethrow and wedge the batch until it succeeds (queue preserved; SDK retries)',
      fields,
    )
  } catch {
    // Best-effort: telemetry must never replace the error `uploadData` rethrows.
    // The key stays unlatched so a later retry re-attempts the beacon.
  }
}

/**
 * Isolate a single CRUD entry that can never upload, so `batch.complete()` can
 * drain it instead of the whole queue wedging behind it. Caller MUST gate this
 * on `isPermanentUploadError(err)`.
 *
 * `capture_items` carry a surveyor's count, so the payload is preserved in the
 * device-local `orphaned_capture_items` quarantine (recoverable; never a silent
 * drop). floors / spaces are synced reference rows: a server-REJECTED optimistic
 * create has NO authoritative row to sync down and evict the local row, so we
 * DELETE the local row + emit `dropped` (mirroring dropOrphanFloor / the spaces
 * cancelled-origin drop) to prevent a phantom — e.g. the off-catalogue New-Room
 * room lingering with zero counts (C6/#692, audit cycle 2). capture_sessions /
 * asset_types are left for the caller's `batch.complete()` to ack — their
 * optimistic-row lifecycle differs and their rejected-create phantom is a
 * separate, pre-existing concern. (A session that can't be created still has its
 * child counts preserved separately when their own item entries hit the
 * item carve-out.)
 *
 * Never throws on its own happy path — throwing would re-wedge the queue. If
 * the quarantine writes themselves fail (e.g. the DB was closed), that error
 * propagates out of `uploadData` and the SDK retries, which is correct.
 */
export async function isolatePoisonEntry(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  err: unknown,
): Promise<void> {
  const { code, status } = errorCodeStatus(err)
  // A code-less 404 (proxy/CDN/gateway in front of Encore served an HTML/empty
  // body, so apiFetch surfaced status 404 with no Encore `code` — H3/#694) is
  // classified not_found BY STATUS in isPermanentUploadError. Label its
  // disposition distinctly so quarantine/Sentry telemetry separates an infra 404
  // from an application not_found, even though both drain identically. `code` is
  // 'unknown' precisely when err.code was absent (errorCodeStatus), and only an
  // ApiError carries a status, so this pair uniquely identifies the case.
  const reason =
    code === 'unknown' && status === 404
      ? 'powersync_upload_isolated_codeless_404'
      : `powersync_upload_isolated_${code}`

  if (entry.table === 'capture_items') {
    await quarantineUnsyncableCount(
      entry,
      db,
      err instanceof Error ? err : new Error(String(err)),
      entry.opData ?? {},
      reason,
    )
    return
  }

  // floors / spaces: the server REJECTED this optimistic create (off-catalogue
  // floor name → 400 invalid_argument; dependent New-Room room whose floor never
  // landed → 404 not_found). No authoritative row will ever sync down to evict
  // the optimistic local row, so DELETE it + emit `dropped` — the same
  // phantom-prevention dropOrphanFloor / the spaces cancelled-origin path do
  // (C6/#692, audit cycle 2). The resulting DELETE CRUD entry carries no
  // pending_space_deletes tombstone, so it acks LOCALLY next cycle (no server
  // POST) and is idempotent across a batch retry. We deliberately do NOT touch
  // floor_origin_sessions (retained for an idempotent re-drop — audit-#740). Then
  // fall through to the shared Sentry/console telemetry below.
  if (entry.table === 'floors' || entry.table === 'spaces') {
    await db.execute(
      entry.table === 'floors'
        ? `DELETE FROM floors WHERE id = ?`
        : `DELETE FROM spaces WHERE id = ?`,
      [entry.id],
    )
    _emitDropped(entry.table, entry.id, 'superseded')
  }

  Sentry.captureException(err, {
    level: 'warning',
    extra: {
      reason,
      table: entry.table,
      op: entry.op,
      entryId: entry.id,
      isolated: true,
    },
  })
  console.warn(
    '[powersync] permanent upload error isolated to drain the queue (no count to preserve)',
    { table: entry.table, op: entry.op, id: entry.id, code },
  )
}
